import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type BenefitType, type ReferentPriceType } from '@strata/constants';
import { type ReferralStatusIndicator } from '@strata/react-status-indicators';

import { type PendingReferralStatus } from 'services/constants';
import { type ReferralValueCase } from 'store/api/performanceApi';

export interface PendingReferral {
    referralId: string;
    payerId: string;
    segmentId: string;
    productServiceOid: string;
    product: string;
    healthPlan: string;
    segmentName: string;
    incentiveTotal?: number;
    routeCode: string;
    referralStatus: (typeof ReferralStatusIndicator.Statuses)[keyof typeof ReferralStatusIndicator.Statuses];
    valueCases: ReferralValueCase[];
    offerRate?: string;
    pendingExpiration: string;
    friendlyId?: string;
    benefit: BenefitType;
    placeOfService: string;
    isBrand: boolean;
    referentPriceType: ReferentPriceType;
    priceIsFrozen: boolean;
    priceSource: string;
    referralExpiration: string;
    pendingStatus: (typeof PendingReferralStatus)[keyof typeof PendingReferralStatus];
    pendingAcceptedRate?: string;
}

export interface WonReferralListSliceState {
    pendingReferralData?: PendingReferral[];
}

const initialState: WonReferralListSliceState = { pendingReferralData: undefined };

export const wonReferralListSlice = createSlice({
    name: 'wonReferralList',
    initialState,
    reducers: {
        setPendingReferralData(
            state,
            action: PayloadAction<{ incomingReferrals: PendingReferral[] }>,
        ) {
            const { incomingReferrals } = action.payload;

            // Update referrals that exist and append new referrals
            const referralsToUpdate: Record<string, PendingReferral> = {};
            const newReferrals: PendingReferral[] = [];
            incomingReferrals.forEach((incomingReferral) => {
                if (
                    state.pendingReferralData?.find(
                        (existingReferral) =>
                            incomingReferral.referralId === existingReferral.referralId,
                    )
                ) {
                    referralsToUpdate[incomingReferral.referralId] = incomingReferral;
                } else {
                    newReferrals.push(incomingReferral);
                }
            });

            return {
                ...state,
                pendingReferralData: [
                    ...(state.pendingReferralData ?? []).map(
                        (referral) => referralsToUpdate[referral.referralId] ?? referral,
                    ),
                    ...newReferrals,
                ],
            };
        },
        setPendingReferralStatus(
            state,
            action: PayloadAction<
                Pick<
                    PendingReferral,
                    'referralId' | 'referralStatus' | 'pendingStatus' | 'pendingAcceptedRate'
                >
            >,
        ) {
            const { referralId, referralStatus, pendingStatus, pendingAcceptedRate } =
                action.payload;
            return {
                ...state,
                pendingReferralData: state.pendingReferralData?.map((referral) =>
                    referral.referralId === referralId
                        ? {
                              ...referral,
                              referralStatus,
                              pendingStatus,
                              pendingAcceptedRate,
                          }
                        : referral,
                ),
            };
        },
    },
});

export const { setPendingReferralData, setPendingReferralStatus } = wonReferralListSlice.actions;
